import { useContext, useMemo } from "react";
import {
  BlankMediaTypeProfessionalEquipApplication,
  EquipmentItemViewModel,
  ItemViewModel,
  SubmitBlankMediaProfessionalUserApplication,
} from "../../../../../../generated/data-contracts";
import {
  IAvisEditableListFieldConfig,
  IRowState,
} from "../../../../../shared/components/editableList";
import {
  AvisDropdown,
  AvisInputInt,
  AvisInputText,
} from "../../../../../shared/components/inputs";
import { AvisContext } from "../../../../../shared/context";
import { ApplicationSectionBase, ISectionSharedProps } from "../../forms";
import { indexArrayByKey } from "../../../../../../utils";

interface IBlankMediaApplicationEquipApplicationsProps
  extends ISectionSharedProps<SubmitBlankMediaProfessionalUserApplication> {
  importersAndManufacturers: ItemViewModel[] | undefined;
}
export function BlankMediaApplicationEquipApplications(
  props: Readonly<IBlankMediaApplicationEquipApplicationsProps>,
) {
  const {
    bag,
    bagField,
    equipments,
    disabled,
    loading,
    importersAndManufacturers,
  } = props;
  const { res } = useContext(AvisContext);

  const availablePersonsMap = useMemo(
    () => indexArrayByKey(importersAndManufacturers || [], "id"),
    [importersAndManufacturers],
  );

  const fieldCollection: IAvisEditableListFieldConfig<
    IRowState<BlankMediaTypeProfessionalEquipApplication>
  >[] = useMemo(
    () => [
      {
        name: "personId",
        title: res("blankMedia_Application_label_ImporterAndManufacturer"),
        required: true,
        width: 400,
        maxWidth: 200,
        transformViewValue: (fieldValue) => {
          return (
            availablePersonsMap.get(fieldValue)?.name ?? `id:${fieldValue}`
          );
        },
        renderField: (field, prefix, i, val, onChange) => {
          const fieldName = `${prefix}.${i}.${field.name}`;

          return (
            <AvisDropdown<ItemViewModel>
              name={fieldName}
              onChange={onChange}
              options={importersAndManufacturers}
              optionValue="id"
              optionLabel="name"
              value={val.personId}
              hideClear
              required={field.required}
              filter
              emptyFilterMessage={res("message_NoRelevantItems")}
              disabled={disabled}
            />
          );
        },
      },
      {
        name: "equipmentId",
        title: res("blankMedia_Application_label_Equipment"),
        required: true,
        width: 250,
        transformViewValue: (fieldValue) => {
          return (
            equipments?.filter((item) => item.id == fieldValue)?.[0]?.name ?? ""
          );
        },
        renderField: (field, prefix, i, val, onChange) => {
          return (
            <AvisDropdown<EquipmentItemViewModel>
              name={`${prefix}.${i}.${field.name}`}
              className="equipment-dropdown"
              onChange={onChange}
              options={equipments}
              optionValue="id"
              optionLabel="name"
              value={val[field.name]}
              hideClear
              required={field.required}
              disabled={disabled}
            />
          );
        },
      },

      {
        name: "balance",
        title: res("blankMedia_Application_label_Balance"),
        required: true,
        width: 75,
        renderField: (field, prefix, i, val, onChange) => {
          return (
            <AvisInputInt
              name={`${prefix}.${i}.${field.name}`}
              value={val[field.name]?.toString()}
              onChange={onChange}
              disabled={disabled}
              required={field.required}
              hideClear
            />
          );
        },
      },
      {
        name: "purchased",
        title: res("blankMedia_Application_label_Purchased"),
        required: true,
        width: 75,
        renderField: (field, prefix, i, val, onChange) => {
          return (
            <AvisInputInt
              name={`${prefix}.${i}.${field.name}`}
              value={val[field.name]?.toString()}
              onChange={onChange}
              disabled={disabled}
              required={field.required}
              hideClear
            />
          );
        },
      },
      {
        name: "spent",
        title: res("blankMedia_Application_label_Spent"),
        required: true,
        width: 75,
        renderField: (field, prefix, i, val, onChange) => {
          return (
            <AvisInputInt
              name={`${prefix}.${i}.${field.name}`}
              value={val[field.name]?.toString()}
              onChange={onChange}
              disabled={disabled}
              required={field.required}
              hideClear
            />
          );
        },
      },
      {
        name: "utilizationOfEachUnit",
        title: res("blankMedia_Application_label_UtilizationOfEachUnit"),
        required: true,
        width: 400,
        renderField: (field, prefix, i, val, onChange) => {
          return (
            <AvisInputText
              name={`${prefix}.${i}.${field.name}`}
              value={val[field.name]?.toString()}
              onChange={onChange}
              maxLength={200}
              disabled={disabled}
              required={field.required}
              hideClear
            />
          );
        },
      },
    ],
    [res, availablePersonsMap, importersAndManufacturers, disabled, equipments],
  );

  return (
    <ApplicationSectionBase
      title={res("blankMedia_Application_section_P_EquipApplications")}
      textToDisplay="portal_TDN_ProfesionalReport_Purchased"
      bag={bag}
      bagField={bagField}
      initValue={{} as IRowState<BlankMediaTypeProfessionalEquipApplication>}
      fieldCollection={fieldCollection}
      loading={loading}
      forceUpdateKey={importersAndManufacturers}
      disabled={disabled}
    />
  );
}
